import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IMenuItem } from '@src/slide/application/service/menu-manager/menu-items/menu-items.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import * as TopMenu from '@editor/src/js/constants/top-menu-data';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { isAttachmentShapeModel, isAttachmentImageElement, isDocAttachmentElement, isIframeShapeModel, isUiModelIsAttachment } from '@src/slide/domain/ui-model/elements/element-judge';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';
import type { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { getAllImage } from '../menu-values/element-menu-values-getter';
import { FillType, IImageFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import {
    AlignType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import {
    ChartElementType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';

interface IDisableMenuItemServices {
    editorInstanceService: IEditorInstanceService;
}
const ONLY_ONE = 1;

/**
 * 获取禁用菜单列表
 * @param model uiModel
 */
// eslint-disable-next-line complexity
export function getDisableMenuItems(
    model: IBaseModel | INoteModel | ISlideModel,
    services: IDisableMenuItemServices,
): IMenuItem[] {
    const modelType = model.type;
    // 图表
    if ([UIModelType.CHART].includes(modelType)) {
        return getChartDisableMenuItems(model as IChartModel);
    }
    // 空白
    if ([UIModelType.EMPTY].includes(modelType)) {
        return TopMenu.NoneSelectDisableItems;
    }
    // 连接线
    if ([UIModelType.CONNECTOR].includes(modelType)) {
        return TopMenu.LineSelectDisableItems;
    }
    // 左侧缩略图间隔条
    if ([UIModelType.GAP].includes(modelType)) {
        return TopMenu.NoneSelectDisableItems;
    }
    // 组合
    if ([UIModelType.GROUP].includes(modelType)) {
        return multiGroupDisableItems(getAllChildModels(model as IGroupModel, true), services, model as IGroupModel);
    }
    if ([UIModelType.SMART_ART].includes(modelType)) {
        return getSmartArtDisabledItems(model);
    }
    // 备注
    if ([UIModelType.NOTE].includes(modelType)) {
        return TopMenu.NoteDisabledItems;
    }
    // 图片
    if ([UIModelType.IMAGE].includes(modelType)) {
        // 图片类附件
        if (isAttachmentImageElement(model)) {
            return TopMenu.AttachSelectDisableItems.concat([
                { id: ActionType.INSERT_ANIMATION },
            ]);
        }

        if (!(model as IPictureModel).isMedia()) {
            return TopMenu.PicSelectDisableItems;
        }

        // 支持视频裁剪
        if ((model as IPictureModel).isVideo()
            && DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.tailorVideo)
        ) {
            return TopMenu.AttachSelectDisableItems.filter(item => item.id !== ActionType.TAILOR_PIC);
        }

        return TopMenu.AttachSelectDisableItems;
    }
    // slide/master/layout
    if ([UIModelType.SLIDE, UIModelType.MASTER, UIModelType.LAYOUT].includes(modelType)) {
        return TopMenu.SlideSelectDisableItems;
    }
    // 表格
    if ([UIModelType.TABLE].includes(modelType)) {
        return getTableDisableMenuItems(model as ITableModel, services.editorInstanceService);
    }
    // 形状
    if ([UIModelType.SHAPE].includes(modelType)) {
        return getShapeDisableMenuItems(model as IShapeModel);
    }
    // 多选图形
    if ([UIModelType.MULTIPLY].includes(modelType)) {
        return getMultiplyDisableMenuItems(model as IMultiplyModel, services);
    }
    return [];
}


/**
 * 获取smartArt禁用菜单列表
*/
function getSmartArtDisabledItems(model: IBaseModel): IMenuItem[] {
    const allDisableItems: { id: string }[] = [];
    // 顶部禁用工具栏
    allDisableItems.concat(TopMenu.SmartArtSelectDisableItems);

    // 右键禁用菜单栏
    const egGroupShapeMsDspData = model?.sdkData?.props?.diagram?.drawing?.spTree?.egGroupShapeMsDsp;
    /* 以下二种情况暂时屏蔽组合内的smartart“转成组合”功能
    *1、导入一个由多个smartArt组成的组合；防止多层嵌套导致冲突处理出错，与office保持一致
    *2、smartart数据中txXfrm属性中有rotate，目前shape中并未此属性，防止转化成组合后文字渲染出错
    */
    const isHastxxfrmRotate = (egGroupShapeMsDspData) => {
        // 检查参数是否为数组并且不为空
        if (!Array.isArray(egGroupShapeMsDspData) || egGroupShapeMsDspData.length === 0) {
            console.error('Invalid input: The parameter should be a non-empty array.');
            return false;
        }
        for (let i = 0; i < egGroupShapeMsDspData.length; i++) {
            if (egGroupShapeMsDspData[i]?.sp && egGroupShapeMsDspData[i]?.sp?.txXfrm
                && egGroupShapeMsDspData[i]?.sp?.txXfrm?.rot !== undefined) {
                return true;
            }
        }
        return false;
    };
    if (model.parent?.type === UIModelType.GROUP || isHastxxfrmRotate(egGroupShapeMsDspData)) {
        allDisableItems.push({ id: ActionType.CONVERT_TO_GROUP });
    }
    return allDisableItems;
}
/**
 * 获取表格禁用菜单列表
 * @param model
 */
function getTableDisableMenuItems(model: ITableModel, editorInstanceService: IEditorInstanceService): IMenuItem[] {
    const allDisableItems: { id: string }[] = [];
    if (!model.canMergeSelectedCells()) {
        allDisableItems.push({ id: ActionType.MERGE_CELL });
    }
    let disableItems = TopMenu.TableSelectDisableItems;
    if (editorInstanceService.checkEditorIsEditing()) {
        // 表格在文本框有焦点时，不过滤链接
        disableItems = disableItems.filter(item => item.id !== ActionType.LINK);
    }
    return allDisableItems.concat(disableItems);
}

function getChartDisableMenuItems(model: IChartModel): IMenuItem[] {
    if (model.isElementSelected(ChartElementType.TITLE)) {
        // 在进行富文本编辑的时候，放开顶部栏
        return TopMenu.ChartTextEditingDisableItems;
    }

    return TopMenu.ChartSelectDisableItems;
}

/**
 * 获取形状禁用菜单列表
 * @param model
 */
function getShapeDisableMenuItems(model: IShapeModel): IMenuItem[] {
    if (model.isAttachment()) {
        if (model.isAttachmentUploaded()) {
            // 文字链模式的所有附件和卡片模式下的文档附件禁用动画
            if (isDocAttachmentElement(model) || isAttachmentShapeModel(model)) {
                return TopMenu.AttachSelectDisableItems
                    .concat([{ id: ActionType.INSERT_ANIMATION }]);
            }
            return TopMenu.AttachSelectDisableItems;
        }
        return [{ id: ActionType.INSERT_ANIMATION }].concat(TopMenu.AttachSelectDisableItems);
    }
    const isIframe = model.props?.addon?.displayMode === AddonMode.IFRAME || false;
    let disableData = isIframe ? TopMenu.AttachSelectDisableItems : TopMenu.TextSelectDisableItems;
    // 特殊逻辑，背景是图片也enable裁剪
    const fillType = model.getBackgroundFill().getFillType();
    if (fillType === FillType.ImageFill && !model.isTextMath) { // 平铺状态也不支持裁剪
        const image = model.getBackgroundFill().getCurrentFill()?.fillInfo as IImageFill;
        if (!image.tile) {
            disableData = TopMenu.TextImageFillSelectDisableItems;
        } else {
            disableData = TopMenu.TextImageTileFillSelectDisableItems;
        }
    }
    return disableData;
}

/**
 * 获取多选图形禁用菜单列表
 * @param model
 */
function getMultiplyDisableMenuItems(model: IMultiplyModel, services: IDisableMenuItemServices): IMenuItem[] {
    // 多选时可能不支持的操作项
    const unsupportedItems = [
        ActionType.FONT,
        ActionType.FONTSIZE,
        ActionType.FONTSIZE_INCREASE,
        ActionType.BOLD,
        ActionType.ITALIC,
        ActionType.UNDERLINE,
        ActionType.DELETELINE,
        ActionType.SUBSCRIPT,
        ActionType.SUPERSCRIPT,
        AlignType.HORIZONTAL_LEFT,
        AlignType.HORIZONTAL_CENTER,
        AlignType.HORIZONTAL_RIGHT,
        AlignType.HORIZONTAL_JUSTIFY,
        AlignType.HORIZONTAL_DISTRIBUTE,
        ActionType.FORECOLOR,
        ActionType.BACKCOLOR,
        ActionType.FILLCOLOR,
        ActionType.BORDERCOLOR,
        ActionType.HORIZONTAL_ALIGN,
        ActionType.VERTICAL_ALIGN,
        ActionType.ITEMNUMBER,
        ActionType.DIGITNUMBER,
        ActionType.ROW_SPACE,
        ActionType.PARAGRAPH_MORE,
        ActionType.BORDERSTYLE,
        ActionType.PIC_FORMAT,
        ActionType.INSERT_COMMENT,
    ];
    // 垂直对齐子项
    const verticalAlignSubMenu = [
        AlignType.VERTICAL_TOP,
        AlignType.VERTICAL_CENTER,
        AlignType.VERTICAL_BOTTOM,
    ];
    const iframeCount = model.selectedModels.filter(model => isIframeShapeModel(model)).length;
    if (iframeCount > 0) {
        unsupportedItems.push(ActionType.SHAPE_FORMAT);
    }
    // 如果操作项有值，说明是可用的
    const itemValues = getMenuValues(model);
    Object.keys(itemValues).forEach((key) => {
        const index = unsupportedItems.indexOf(key);
        if (index >= 0) {
            unsupportedItems.splice(index, 1);
        }
    });

    // 如果多选的包含附件，禁止所有附件不支持的菜单项
    if (model.selectedModels.some(model => isUiModelIsAttachment(model))) {
        TopMenu.AttachSelectDisableItems.forEach(({ id }) => unsupportedItems.push(id));
    }

    // 如果多选有且不仅一个图片，透明度置灰
    const imageLength = getAllImage(model).length;
    if (imageLength && imageLength !== ONLY_ONE) {
        unsupportedItems.push(ActionType.TRANSPARENCY);
    }

    // 如果垂直对齐置灰，那么其子项也置灰
    if (unsupportedItems.includes(ActionType.VERTICAL_ALIGN)) {
        unsupportedItems.push(...verticalAlignSubMenu);
    }

    // 生成不可用id数据
    const allDisableItems = unsupportedItems.map(item => ({ id: item }));
    const disableItems = multiGroupDisableItems(model.selectedModels, services, model);
    return allDisableItems.concat(disableItems);
}

export function multiGroupDisableItems(
    modelsArray, services: IDisableMenuItemServices,
    model: IGroupModel | IMultiplyModel,
) {
    if (modelsArray) {
        const disableItems = {};
        modelsArray.forEach((model) => {
            disableItems[model.type] = getDisableMenuItems(model, services);
        });
        // 合并不同type的disableItem
        const obj2Array = Object.values(disableItems);
        if (obj2Array.length > 0) {
            // eslint-disable-next-line no-use-before-define
            const mergeData: any = obj2Array.filter((item, index) => index !== 0)
                .reduce((total, item) => mergeItems(total, item), obj2Array[0]);
            let result = TopMenu.GroupSelectDisableItems.concat(mergeData);
            const imageLength = getAllImage(model).length;
            if (imageLength) {
                result = imageLength === ONLY_ONE
                    ? result.filter(item => ![ActionType.FORMAT_PIC, ActionType.PIC_FORMAT, ActionType.TRANSPARENCY]
                        .includes(item.id))
                    : result.filter(item =>  item.id !== ActionType.FORMAT_PIC);
            }
            return result;
        }
    }
    return [];
}

function mergeItems(itemsA, itemsB) {
    const returnItems: any[] = [];
    if (itemsA && itemsB) {
        itemsA.forEach((item) => {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < itemsB.length; i++) {
                if (item.id === itemsB[i].id) {
                    returnItems.push(item);
                    break;
                }
            }
        });
    }
    return returnItems;
}
